import { SalesNotificationPreferences } from '@/pages/sales-team/SalesNotificationPreferences';
import { SalesInviteCard } from '../sales-team/SalesInviteCard';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  listAgents, fetchMyAccount, updateMyName, updateMyPhone,
  type AgentRow,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { SalesPartnerConnectBanner } from '@/components/sp/SalesPartnerConnectBanner';
import { TaxCertificatesCard } from '@/components/sp/TaxCertificatesCard';
import { InlineEditRow } from '@/components/profile/InlineEditRow';
import styles from './SalesPartnerProfilePage.module.css';

/**
 * Phase 7g.4b.4 Commit 2 , Sales Partner My Profile page.
 * Phase 7g.4c Commit C , refactored to consume the shared
 * InlineEditRow component. The local NameRow / PhoneRow state
 * machines moved into apps/admin/src/components/profile/
 * InlineEditRow.tsx; the side-effect handlers (auth store sync
 * on name, local state on phone) stay here as the parent's
 * onSave callbacks.
 *
 * Inline-edit profile surface for SPs. Two editable fields (name +
 * phone) live as separate rows; each row has a read mode and an
 * edit mode. Read mode renders "Field: value" + pencil edit button.
 * Click flips that row to a single-field input plus Save / Cancel.
 * Save fires PATCH for that row's field only and reverts to read
 * mode on success. Each row owns its own save state so a phone-
 * save failure does not lock the name row.
 *
 * Data sources:
 *   - name: lives on the PublicUser snapshot in the auth store.
 *     Save calls updateMyName(name) and optimistically syncs the
 *     auth store via updateUser({ name }) so the TopNav UserChip
 *     reflects the new value immediately without a /auth/me
 *     refetch.
 *   - phone: not on PublicUser. Page seeds local state from a
 *     GET /me/account query on mount; Save calls updateMyPhone and
 *     updates local state with the server response. The auth store
 *     stays untouched for phone changes.
 *
 * Validation: phone enforces E.164 client-side via the same regex
 * the server uses (^\+[1-9]\d{1,14}$). Name enforces minLength 1
 * client-side; server also enforces minLength 1 + maxLength 200.
 *
 * Save failure handling: the row stays in edit mode with the
 * server's error message (extracted via getDisplayErrorMessage
 * inside InlineEditRow) or a fallback inline. User can retry
 * without losing input.
 *
 * Out of scope: editing email, avatar, or any other field;
 * password change (lives on a separate surface); account deletion.
 */

const E164_RE = /^\+[1-9]\d{1,14}$/;

function validateName(value: string): string | null {
  if (value.trim().length === 0) return 'Name cannot be empty.';
  if (value.trim().length > 200) return 'Name must be 200 characters or fewer.';
  return null;
}

function validatePhone(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return 'Phone is required.';
  if (!E164_RE.test(trimmed)) {
    return 'Phone must be in international format, like +14155552671.';
  }
  return null;
}

export function SalesPartnerProfilePage() {
  useRecentRoute('My Profile');
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const agentId = user?.agent_id ?? null;

  const agentsQ = useQuery({
    queryKey: ['sp-profile', 'agents', agentId],
    queryFn: listAgents,
    enabled: !!agentId,
    staleTime: 30_000,
  });
  const accountQ = useQuery({
    queryKey: ['sp-profile', 'account'],
    queryFn: fetchMyAccount,
    staleTime: 30_000,
  });

  const agent: AgentRow | null = useMemo(() => {
    if (!agentsQ.data || !agentId) return null;
    return agentsQ.data.find((a) => a.id === agentId) ?? null;
  }, [agentsQ.data, agentId]);

  // Phone in local state, seeded from the /me/account fetch.
  // PublicUser does not carry phone, so the auth store is not the
  // right home. Re-seeds on background refetch; the InlineEditRow
  // internally guards against stomping a mid-edit draft.
  const [localPhone, setLocalPhone] = useState('');
  useEffect(() => {
    if (accountQ.data && accountQ.data.phone !== null) {
      setLocalPhone(accountQ.data.phone);
    }
  }, [accountQ.data]);

  const handleSaveName = async (newName: string) => {
    await updateMyName(newName);
    updateUser({ name: newName });
  };

  const handleSavePhone = async (newPhone: string) => {
    const updated = await updateMyPhone(newPhone);
    setLocalPhone(updated.phone ?? '');
  };

  // Profile-incomplete signal (replaces the old full-screen no-agent
  // early-return). The name/phone fields read from the user store +
  // /me/account (user-scoped, not agent-gated), so the page renders
  // fully even before provisioning; the SalesPartnerConnectBanner
  // surfaces the setup prompt as a non-blocking banner.
  const profileIncomplete = (user?.is_sales_agent ?? false) && !agentId;

  return (
    <div className={styles.page}>
      <div className={styles.bannerStack}>
        {/* early: the quiet "set up before your first payment" row lives here, the partner's own settings. */}
        <SalesPartnerConnectBanner
          agent={agent}
          agentId={agentId}
          profileIncomplete={profileIncomplete}
          early
        />
      </div>

      <PageHeader programName={agent?.program_info?.name ?? 'Sales Partner'} />

      <div className={styles.fieldGroup}>
        <InlineEditRow
          label="Name"
          value={user?.name ?? ''}
          validator={validateName}
          onSave={handleSaveName}
          maxLength={200}
          emptyText="(not set)"
        />
        <InlineEditRow
          label="Phone"
          value={localPhone}
          inputType="tel"
          placeholder="+14155552671"
          validator={validatePhone}
          onSave={handleSavePhone}
          emptyText="(not set)"
          inputAriaLabel="Phone (international format)"
        />
      </div>

      <div className={styles.identityBlock}>
        <p className={styles.identityRow}>
          <span className={styles.identityLabel}>Email</span>
          <span className={styles.identityValue}>{user?.email ?? 'Not available'}</span>
        </p>
        <p className={styles.identityHint}>
          Email cannot be changed here. Contact your administrator to update it.
        </p>
      </div>

      {/* Resale and exemption certificates per state (Warren, final ruling 5): the account owner only, as the Api allows. */}
      {agentId && <TaxCertificatesCard />}
    </div>
  );
}

function PageHeader({ programName }: { programName: string }) {
  return (
    <header className={styles.header}>
      <h1 className={styles.title}>My Profile</h1>
      <SalesInviteCard /><SalesNotificationPreferences />
      <p className={styles.subtitle}>Your {programName} contact details.</p>
    </header>
  );
}
